import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../Icon';
import ArchivedToggle from '../ui/ArchivedToggle';
import FilterPopover from '../ui/FilterPopover';
import IconButton from '../ui/IconButton';
import ExportButton from './ExportButton';
import { useGuestsBar, setGuestsBar, GUEST_MERGE_OPEN, GUEST_BULK_ADD_OPEN } from '../../hooks/useGuestsBar';
import { useRole } from '../../hooks/useRole';
import { ALL_SIDES } from '../../utils/guestSide';
import { GUEST_ROLE_OPTIONS } from '../../utils/guestRoles';
import { GUEST_NEW_PATH, GUEST_RSVPS_PATH } from '../../utils/guestRoutes';

const RSVP_OPTIONS = [
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'pending', label: 'Pending' },
  { value: 'declined', label: 'Declined' },
];
const ROLE_OPTIONS = GUEST_ROLE_OPTIONS.filter(o => o.value).map(o => ({ ...o }));

/**
 * Breadcrumb actions of the Guests list, as on the other list pages: one Filters popover (Side, RSVP, Tags,
 * Role), the Archived switch, icon buttons for Export, Merge duplicates and Bulk add (tooltip and accessible name), and
 * New guest as the one labelled primary button. Planner actions are absent for a viewer.
 */
export const GuestsListBar: React.FC = () => {
  const navigate = useNavigate();
  const bar = useGuestsBar();
  const { isPlanner } = useRole();

  return (
    <>
      <FilterPopover
        selects={[{
          key: 'side', label: 'Side', options: bar.sideOptions, value: bar.side, defaultValue: ALL_SIDES,
          onChange: side => setGuestsBar({ side }),
        }]}
        toggles={[{ key: 'any-pending', label: 'Has any pending answer', chipLabel: 'Any pending', checked: bar.anyPending, onChange: anyPending => setGuestsBar({ anyPending }) }]}
        sections={[
          { key: 'rsvp', label: 'RSVP', options: RSVP_OPTIONS, values: bar.rsvp, onChange: rsvp => setGuestsBar({ rsvp }) },
          { key: 'tags', label: 'Tags', options: bar.tagOptions, values: bar.tags, onChange: tags => setGuestsBar({ tags }) },
          { key: 'role', label: 'Role', options: ROLE_OPTIONS, values: bar.roles, onChange: roles => setGuestsBar({ roles }) },
        ]}
      />
      <div className="header-filter-group">
        <ArchivedToggle checked={bar.archived} onChange={archived => setGuestsBar({ archived })} />
      </div>
      <ExportButton exportKey="guests" />
      {isPlanner && !bar.archived && (
        <IconButton
          icon="exchange"
          label={bar.duplicateGroups > 0 ? `Merge duplicates (${bar.duplicateGroups})` : 'Merge duplicates'}
          onClick={() => window.dispatchEvent(new CustomEvent(GUEST_MERGE_OPEN))}
        />
      )}
      {isPlanner && !bar.archived && (
        <IconButton icon="user-check" label="Edit RSVPs" onClick={() => navigate(GUEST_RSVPS_PATH)} />
      )}
      {isPlanner && !bar.archived && (
        <IconButton icon="upload" label="Bulk add guests" onClick={() => window.dispatchEvent(new CustomEvent(GUEST_BULK_ADD_OPEN))} />
      )}
      {isPlanner && !bar.archived && (
        <button type="button" className="header-action-btn header-action-btn--iconic primary" onClick={() => navigate(GUEST_NEW_PATH)} title="New guest">
          <Icon name="plus" size={16} />
          <span className="header-action-btn__label">New guest</span>
        </button>
      )}
    </>
  );
};

export default GuestsListBar;
